import { useMemo } from 'react'
import css from './SectionTask.module.css'
import Checkbox from '@/components/ui/Checkbox/Checkbox'
import { useWorkspace } from '../../../../../contexts/WorkspaceContext'
import type { SectionTask as SectionTaskType } from '../../types'
import SectionTaskTitle from './SectionTaskTitle'
import { useSortable } from '@dnd-kit/react/sortable'
import { resolveTaskLabels, type ResolvedLabel } from '@/utils/taskLabels'

interface SectionTaskProps {
    task: SectionTaskType
    index: number
    listId?: string
    onToggleComplete: (taskId: string, isCompleted: boolean) => void
    onAssign?: (taskId: string, assigneeId: string | null) => void
    draggable?: boolean
    onLabelClick?: (labelId: string) => void
    activeLabelIds?: string[]
}

interface DraggableTaskProps {
    task: SectionTaskType
    index: number
    listId: string
    onToggleComplete: (taskId: string, isCompleted: boolean) => void
    onAssign?: (taskId: string, assigneeId: string | null) => void
    statusColor: string | undefined
    assignee: ReturnType<ReturnType<typeof useWorkspace>['getMember']>
    labels: ResolvedLabel[]
    onLabelClick?: (labelId: string) => void
    activeLabelIds?: string[]
}

const DraggableSectionTask = ({ task, index, listId, onToggleComplete, onAssign, statusColor, assignee, labels, onLabelClick, activeLabelIds }: DraggableTaskProps) => {
    const { ref, handleRef, isDragging } = useSortable({
        id: task.id,
        group: listId,
        type: 'item',
        accept: 'item',
        index,
        feedback: 'clone',
        data: { group: listId },
    })

    return (
        <div ref={ref as any} className={`${css.item}${isDragging ? ` ${css.item_dragging}` : ''}`} data-testid="task-item" data-completed={String(task.isCompleted)} data-pending={task.pending ? 'true' : undefined}>
            <div className={css.checkbox} data-testid="task-checkbox">
                <Checkbox checked={task.isCompleted} onChange={(checked) => onToggleComplete(task.id, checked)} />
            </div>
            <div ref={handleRef as any} className={css.drag_area}>
                <SectionTaskTitle
                    name={task.name}
                    assignee={assignee}
                    statusColor={statusColor}
                    isCompleted={task.isCompleted}
                    number={task.number}
                    taskId={task.id}
                    pending={task.pending}
                    onAssign={onAssign ? (userId) => onAssign(task.id, userId) : undefined}
                    labels={labels}
                    onLabelClick={onLabelClick}
                    activeLabelIds={activeLabelIds}
                />
            </div>
        </div>
    )
}

const SectionTask = ({
    task,
    index,
    listId,
    onToggleComplete,
    onAssign,
    draggable: isDraggable,
    onLabelClick,
    activeLabelIds,
}: SectionTaskProps) => {
    const { getStatusColor, getMember, labels: workspaceLabels } = useWorkspace()
    const statusColor = !task.isCompleted ? getStatusColor(task.statusId) : undefined
    const assignee = task.assigneeId ? getMember(task.assigneeId) : null
    const labels = useMemo(
        () => resolveTaskLabels(task.labels, workspaceLabels),
        [task.labels, workspaceLabels]
    )

    if (isDraggable && !task.isCompleted && listId) {
        return (
            <DraggableSectionTask
                task={task}
                index={index}
                listId={listId}
                onToggleComplete={onToggleComplete}
                onAssign={onAssign}
                statusColor={statusColor}
                assignee={assignee ?? undefined}
                labels={labels}
                onLabelClick={onLabelClick}
                activeLabelIds={activeLabelIds}
            />
        )
    }

    return (
        <div className={`${css.item}${task.isCompleted ? ` ${css.item_completed}` : ''}`} data-testid="task-item" data-completed={String(task.isCompleted)} data-pending={task.pending ? 'true' : undefined}>
            <div className={css.checkbox} data-testid="task-checkbox">
                <Checkbox checked={task.isCompleted} onChange={(checked) => onToggleComplete(task.id, checked)} />
            </div>
            <div className={css.drag_area}>
                <SectionTaskTitle
                    name={task.name}
                    assignee={assignee}
                    statusColor={statusColor}
                    isCompleted={task.isCompleted}
                    number={task.number}
                    taskId={task.id}
                    pending={task.pending}
                    onAssign={onAssign ? (userId) => onAssign(task.id, userId) : undefined}
                    labels={labels}
                    onLabelClick={onLabelClick}
                    activeLabelIds={activeLabelIds}
                />
            </div>
        </div>
    )
}

export default SectionTask
